'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import { Skeleton } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow
} from '@repo/ui/components/table';
import {
  TableShell,
  TableShellHead,
  TableShellToolbar
} from '@repo/ui/components/table-shell';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@repo/ui/components/tooltip';
import { toggleSort, type SortState } from '@repo/ui/lib/table-sort';
import { cn } from '@repo/ui/lib/utils';
import { track } from '@/lib/analytics';

import type { InvestorHoldingsResponse } from '../types/api';
import { chainDisplayName } from '@/lib/chain-display-name';
import { formatPercent } from '@/lib/format/percent/percent';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import {
  formatHoldingUnitPrice,
  formatHoldingValue,
  formatTokenUnits,
  holdingDisplayName,
  holdingRowIds,
  KIND_LABELS,
  priceSourceLabel,
  shareColumnLabel,
  tierLabel,
  valuationNotes,
  valueHoldings,
  type CurrencyRegistry
} from '../utils/holding-format';

type SortField = 'position' | 'value' | 'weight';

interface PositionsTableProps {
  /** The page of holdings this table is on. */
  holdings?: InvestorHoldingsResponse;
  /** Names for strategy currency addresses, resolved by the view. */
  currencies?: CurrencyRegistry;
  isLoading?: boolean;
  isError?: boolean;
  /** A page is being fetched; the rows on screen are the previous page's. */
  isFetching?: boolean;
  onPageChange?: (page: number) => void;
}

/**
 * Unpriced last, whatever the direction.
 *
 * A null value means the position's worth is unknown, not zero — sorting it
 * as 0 would bury a real holding under the empty ones on a descending sort
 * and float it to the top on an ascending one. Neither is a statement the
 * data supports, so absence always sinks.
 */
function compareNullableNumber(
  left: number | null,
  right: number | null,
  direction: 'asc' | 'desc'
): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return direction === 'asc' ? left - right : right - left;
}

/**
 * What each column means, in the reader's terms.
 *
 * "Unit price" and "% NAV" are the two people misread most: the first is
 * per share rather than per position, and the second is a share of the whole
 * portfolio rather than of its ROWA part. Both are worth a sentence.
 */
const COLUMN_HELP: Record<string, string> = {
  Position: 'The vault or token you hold. ROWA vaults open their own page.',
  Type: 'Where it sits: a ROWA vault, a token in your wallet, or cash.',
  Units: 'How many shares or tokens you hold.',
  'Unit price':
    'What one share or token is worth right now, and where that price came from.',
  Value:
    'What this holding is worth: units multiplied by the unit price, in the currency the holding is priced in. The dollar amount beneath it uses the current exchange rate.',
  '% NAV': 'How much of your whole portfolio sits in this one holding.',
  '% of valued':
    'How much of the holdings that could be valued in dollars sits in this one holding. Holdings with no price or no exchange rate are not counted, so this is not a share of your whole portfolio.',
  '% of this page':
    'How much of the holdings on this page that could be valued in dollars sits in this one holding. Your portfolio spans more than one page and holds vaults priced in another currency, so a share of the whole portfolio cannot be worked out here.'
};

/** A column name that explains itself on hover or keyboard focus. */
function HeadLabel({ children }: { children: string }) {
  const help = COLUMN_HELP[children];
  if (!help) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="cursor-help">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{help}</TooltipContent>
    </Tooltip>
  );
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, row) => (
        <TableRow key={row}>
          {Array.from({ length: 6 }).map((__, cell) => (
            <TableCell key={cell} className="py-3.5">
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/**
 * Every position, flat and sortable, a page at a time.
 *
 * The Positions card above this reads composition — what the portfolio is
 * made of, grouped by whichever lens is selected, with the tail folded away.
 * That answers "how am I allocated". It does not answer "where is my money,
 * line by line", which is the question someone scanning for a specific
 * holding is asking. This is that list: no grouping, no folding, every
 * holding with its own row.
 *
 * The table owns its pager and says which rows it is showing. Pages arrive
 * largest first from the server; a column sort reorders the rows of the
 * page on screen, and the table says so when there is more than one page.
 */
export function PositionsTable({
  holdings,
  currencies,
  isLoading = false,
  isError = false,
  isFetching = false,
  onPageChange
}: PositionsTableProps) {
  const [sort, setSort] = useState<SortState<SortField>>({
    field: 'value',
    direction: 'desc'
  });

  const served = useMemo(() => holdings?.holdings ?? [], [holdings]);
  const rowIds = useMemo(() => holdingRowIds(served), [served]);
  // Each row in its own currency, with its dollar value where a rate is
  // served, and its share on the same basis as the total it is a share of.
  // A ROWA row's `valueUsd` and `weightBps` are strategy currency figures
  // under dollar names, so nothing here reads them directly.
  const valuation = useMemo(
    () => (holdings ? valueHoldings(holdings, currencies) : null),
    [holdings, currencies]
  );
  const notes = useMemo(
    () => (valuation ? valuationNotes(valuation) : []),
    [valuation]
  );

  const rows = useMemo(() => {
    const all = [...served];
    return all.sort((left, right) => {
      if (sort.field === 'position') {
        const compared = holdingDisplayName(left).localeCompare(
          holdingDisplayName(right)
        );
        return sort.direction === 'asc' ? compared : -compared;
      }
      if (sort.field === 'weight') {
        return compareNullableNumber(
          valuation?.shares.get(left) ?? null,
          valuation?.shares.get(right) ?? null,
          sort.direction
        );
      }
      // Compared in dollars. A row with no price, or no exchange rate,
      // has no dollar value to compare and sinks.
      return compareNullableNumber(
        valuation?.values.get(left)?.usd ?? null,
        valuation?.values.get(right)?.usd ?? null,
        sort.direction
      );
    });
  }, [served, valuation, sort]);

  function changeSort(field: SortField) {
    const next = toggleSort(sort, field);
    setSort(next);
    track('portfolio.positions_sorted', {
      field: next.field,
      direction: next.direction
    });
  }

  const directionFor = (field: SortField) =>
    sort.field === field ? sort.direction : null;

  const isEmpty =
    !isLoading && !isError && (holdings?.coverage.holdingsTotal ?? 0) === 0;

  const page = holdings?.page ?? 1;
  const total = holdings?.total ?? 0;
  const totalPages = holdings
    ? Math.max(1, Math.ceil(holdings.total / holdings.limit))
    : 1;
  const hasPages = totalPages > 1;
  const firstRow = holdings ? (page - 1) * holdings.limit + 1 : 0;
  const lastRow = firstRow + served.length - 1;

  return (
    <TableShell role="region" aria-labelledby="positions-table-title">
      <TableShellToolbar>
        <div>
          <h3
            id="positions-table-title"
            className="text-rowa-card text-rowa-text-primary"
          >
            All positions
          </h3>
          <p className="text-rowa-label-sm text-rowa-text-tertiary mt-0.5">
            {hasPages
              ? `Every holding, line by line, ${holdings!.limit} to a page. Sorting a column reorders the page on screen.`
              : 'Every holding, line by line'}
          </p>
        </div>
        {holdings && !isLoading && !isError && total > 0 ? (
          <p
            className="text-rowa-label-sm text-rowa-text-tertiary tabular-nums"
            aria-live="polite"
          >
            {hasPages
              ? `Showing ${firstRow} to ${lastRow} of ${total} positions`
              : `${total} position${total === 1 ? '' : 's'}`}
          </p>
        ) : null}
      </TableShellToolbar>

      {isError ? (
        <p className="text-rowa-body-sm text-rowa-text-secondary px-5 py-10 text-center">
          We could not load your positions. Refresh to try again.
        </p>
      ) : isEmpty ? (
        <p className="text-rowa-body-sm text-rowa-text-secondary px-5 py-10 text-center">
          No holdings are recognized on this network yet.
        </p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table aria-label="All positions" aria-busy={isFetching}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableShellHead
                  className="pl-5"
                  sortDirection={directionFor('position')}
                  onSort={() => changeSort('position')}
                >
                  <HeadLabel>Position</HeadLabel>
                </TableShellHead>
                <TableShellHead>
                  <HeadLabel>Type</HeadLabel>
                </TableShellHead>
                <TableShellHead align="right">
                  <HeadLabel>Units</HeadLabel>
                </TableShellHead>
                <TableShellHead align="right">
                  <HeadLabel>Unit price</HeadLabel>
                </TableShellHead>
                <TableShellHead
                  align="right"
                  sortDirection={directionFor('value')}
                  onSort={() => changeSort('value')}
                >
                  <HeadLabel>Value</HeadLabel>
                </TableShellHead>
                <TableShellHead
                  align="right"
                  className="pr-5"
                  sortDirection={directionFor('weight')}
                  onSort={() => changeSort('weight')}
                >
                  <HeadLabel>{shareColumnLabel(valuation)}</HeadLabel>
                </TableShellHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <LoadingRows />
              ) : (
                rows.map((holding) => {
                  const isVault =
                    holding.kind === 'rowa' && holding.address != null;
                  const value = valuation?.values.get(holding) ?? null;
                  const share = valuation?.shares.get(holding) ?? null;

                  return (
                    <TableRow
                      key={rowIds.get(holding)}
                      className={cn(
                        'hover:bg-rowa-surface-hover/40',
                        // The previous page stays up while the next loads.
                        isFetching && 'opacity-60'
                      )}
                    >
                      <TableCell className="min-w-52 py-3.5 pl-5">
                        {isVault ? (
                          <Link
                            href={`/vaults/${holding.address}`}
                            onClick={() =>
                              track('portfolio.vault_opened', {
                                vault: holding.address!
                              })
                            }
                            className="text-rowa-body-sm text-rowa-text-primary hover:text-rowa-text-link inline-flex max-w-full items-center gap-1 truncate font-semibold"
                          >
                            <span className="truncate">
                              {holdingDisplayName(holding)}
                            </span>
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                          </Link>
                        ) : (
                          <p className="text-rowa-body-sm text-rowa-text-primary truncate font-semibold">
                            {holdingDisplayName(holding)}
                          </p>
                        )}
                        <p className="text-rowa-label-sm text-rowa-text-tertiary truncate">
                          {chainDisplayName(holding.chainId)}
                          {holding.liquidityTier
                            ? ` · ${tierLabel(holding.liquidityTier)}`
                            : ''}
                        </p>
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-secondary">
                        {KIND_LABELS[holding.kind]}
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-primary text-right font-mono tabular-nums">
                        {formatTokenUnits(holding)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-rowa-body-sm text-rowa-text-primary block font-mono tabular-nums">
                          {formatHoldingUnitPrice(
                            holding,
                            currencies,
                            holdings?.fx
                          )}
                        </span>
                        <span className="text-rowa-label-sm text-rowa-text-tertiary block">
                          {priceSourceLabel(holding)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Unpriced is not zero — the position is real, its
                            worth is simply not known. */}
                        <span
                          className={cn(
                            'text-rowa-body-sm block font-mono tabular-nums',
                            value == null
                              ? 'text-rowa-text-tertiary'
                              : 'text-rowa-text-primary'
                          )}
                        >
                          {value == null
                            ? 'Not priced'
                            : formatHoldingValue(value)}
                        </span>
                        {/* A value in another currency also states its
                            dollar worth, or says there is no rate for it.
                            It is never shown one for one. */}
                        {value != null && !value.isDollar ? (
                          <span className="text-rowa-label-sm text-rowa-text-tertiary block tabular-nums">
                            {value.usd != null
                              ? formatCompactUsd(value.usd, {
                                  maximumFractionDigits: 2
                                })
                              : 'No exchange rate'}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-secondary pr-5 text-right font-mono tabular-nums">
                        {formatPercent(share, { digits: 1 })}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {notes.length > 0 && !isError && !isEmpty && !isLoading ? (
        <div className="border-rowa-border-subtle text-rowa-label-sm text-rowa-text-tertiary space-y-1 border-t px-5 py-3">
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      ) : null}

      {hasPages && !isError && !isEmpty ? (
        <nav
          aria-label="All positions pages"
          className="border-rowa-border-subtle flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3"
        >
          <span className="text-rowa-label-sm text-rowa-text-secondary tabular-nums">
            Page {page} of {totalPages}
          </span>
          <span className="flex items-center gap-2">
            <Button
              type="button"
              variant="quiet"
              size="sm"
              disabled={page <= 1 || isFetching}
              onClick={() => onPageChange?.(Math.max(1, page - 1))}
            >
              <ChevronLeft className="size-4" aria-hidden />
              Previous
            </Button>
            <Button
              type="button"
              variant="quiet"
              size="sm"
              disabled={page >= totalPages || isFetching}
              onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
            >
              Next
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </span>
        </nav>
      ) : null}
    </TableShell>
  );
}
